import { useState, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { resetOnboarding, hasAnyOnboardingProgress } from '../lib/onboarding-reset'

// Artigos importados raw via Vite. Pra adicionar um novo: cria o .md em
// content/help/ e adiciona uma entrada aqui (slug + título + descrição).
import enviarDocumento  from '../content/help/enviar-documento.md?raw'
import validadeJuridica from '../content/help/validade-juridica.md?raw'
import precosPlanos     from '../content/help/precos-planos.md?raw'
import pagamentoSignatario from '../content/help/pagamento-signatario.md?raw'
import notaFiscal       from '../content/help/nota-fiscal.md?raw'

export interface HelpArticle {
  slug: string
  title: string
  description: string
  content: string
}

export const HELP_ARTICLES: HelpArticle[] = [
  {
    slug: 'enviar-documento',
    title: 'Como enviar um documento para assinatura',
    description: 'Subir PDF, adicionar signatários, posicionar campos, enviar.',
    content: enviarDocumento,
  },
  {
    slug: 'validade-juridica',
    title: 'Validade jurídica da assinatura PAKT',
    description: 'Base legal: Lei 14.063/2020, PAdES, ICP-Brasil, eIDAS.',
    content: validadeJuridica,
  },
  {
    slug: 'precos-planos',
    title: 'Planos e preços',
    description: 'Pay-per-sign · R$ 3,90 por assinatura confirmada · trial.',
    content: precosPlanos,
  },
  {
    slug: 'pagamento-signatario',
    title: 'Como funciona o pagamento de R$ 3,90 do signatário',
    description: 'Pagamento PIX como prova de identidade bancária.',
    content: pagamentoSignatario,
  },
  {
    slug: 'nota-fiscal',
    title: 'Como obter NFS-e da minha cobrança',
    description: 'Configurar emissão automática · re-emissão · correção.',
    content: notaFiscal,
  },
]

export default function Help() {
  const [query, setQuery] = useState('')
  const navigate = useNavigate()
  const [hasProgress, setHasProgress] = useState(hasAnyOnboardingProgress())
  const [resetMsg, setResetMsg] = useState<string | null>(null)

  function handleResetTour() {
    resetOnboarding()
    setHasProgress(false)
    setResetMsg('Tour resetado. Vá pro Dashboard pra rever as dicas.')
    setTimeout(() => setResetMsg(null), 5000)
  }

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return HELP_ARTICLES
    return HELP_ARTICLES.filter(a =>
      a.title.toLowerCase().includes(q) ||
      a.description.toLowerCase().includes(q) ||
      a.content.toLowerCase().includes(q)
    )
  }, [query])

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold text-indigo-600">PAKT</Link>
          <Link to="/" className="text-sm text-indigo-600 hover:text-indigo-800 transition">
            ← Voltar
          </Link>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Central de Ajuda</h1>
        <p className="text-gray-600 mb-6">
          Respostas pras perguntas mais frequentes. Não achou? Manda e-mail pra{' '}
          <a href="mailto:suporte@pakt.io" className="text-indigo-600 hover:underline">suporte@pakt.io</a>
          {' '}— respondemos em até 24h em dias úteis.
        </p>

        <div className="mb-6">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar nos artigos..."
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
          />
          {query && (
            <p className="text-xs text-gray-500 mt-1">
              {filtered.length} {filtered.length === 1 ? 'resultado' : 'resultados'}
            </p>
          )}
        </div>

        {filtered.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
            <p className="text-gray-500 mb-4">Nada encontrado pra "<strong>{query}</strong>".</p>
            <a
              href={`mailto:suporte@pakt.io?subject=Dúvida: ${encodeURIComponent(query)}`}
              className="inline-block bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm"
            >
              Perguntar pro suporte
            </a>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((article) => (
              <button
                key={article.slug}
                onClick={() => navigate(`/ajuda/${article.slug}`)}
                className="block w-full text-left bg-white rounded-xl border border-gray-200 hover:border-indigo-300 hover:shadow-sm transition p-5"
              >
                <h2 className="text-lg font-semibold text-gray-900 group-hover:text-indigo-600">
                  {article.title}
                </h2>
                <p className="text-sm text-gray-600 mt-1">{article.description}</p>
              </button>
            ))}
          </div>
        )}

        {/* Reset de onboarding — útil pra novo membro da equipe OU
            quem dispensou as dicas e quer rever. Só aparece se o user
            completou pelo menos 1 passo (não atrapalha onboarding ativo). */}
        {hasProgress && (
          <div className="mt-12 pt-8 border-t border-gray-200">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Rever boas-vindas</h3>
            <p className="text-sm text-gray-600 mb-3">
              Quer ver de novo o tour guiado do primeiro documento? Ideal pra novos membros
              da equipe ou pra relembrar onde fica cada coisa.
            </p>
            <button
              onClick={handleResetTour}
              className="text-sm bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg"
            >
              ↻ Resetar tour de boas-vindas
            </button>
            {resetMsg && (
              <p className="text-sm text-green-600 mt-2">{resetMsg}</p>
            )}
          </div>
        )}

        <div className="mt-12 pt-8 border-t border-gray-200">
          <p className="text-sm text-gray-600">
            Procurando documentos legais? Veja{' '}
            <Link to="/termos" className="text-indigo-600 hover:underline">Termos de Uso</Link>,{' '}
            <Link to="/privacidade" className="text-indigo-600 hover:underline">Privacidade</Link> e{' '}
            <Link to="/dpa" className="text-indigo-600 hover:underline">DPA</Link>.
          </p>
        </div>
      </main>
    </div>
  )
}
